import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft, 
  Send, 
  Loader2,
  Bot,
  User,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import { AGENT_CODES } from '../config/environment';
import type { Debtor } from '../types';

interface ChatPageProps {
  debtor: Debtor;
}

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function ChatPage({ debtor }: ChatPageProps) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Context parameters to identify the user to the chatbot
  const debtorContext = {
    debtorId: debtor.debtor_id,
    phone: debtor.phone || '',
    email: debtor.email || '',
    firstName: debtor.first_name || '',
    lastName: debtor.last_name || '',
    language: 'en', // Force English responses
    preferredLanguage: 'en',
  };

  useEffect(() => {
    initializeChat();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create a new conversation with the chatbot, passing debtor context
      // This allows the agent to automatically identify and load the user's data
      const inputParameters = [
        { key: 'debtorId', value: debtor.debtor_id },
        { key: 'phone', value: debtor.phone || '' },
        { key: 'email', value: debtor.email || '' },
        { key: 'firstName', value: debtor.first_name || '' },
        { key: 'language', value: 'en' },
        { key: 'preferredLanguage', value: 'en' },
      ];
      
      const conversation = await serenityClient.createConversation(
        AGENT_CODES.CHATBOT,
        debtor.debtor_id,
        inputParameters
      );
      
      setChatId(conversation.chatId);
      
      // Show the initial message from the agent
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: conversation.content || `Hello ${debtor.first_name}! I'm your DFS Collections assistant. I'm here to help you understand your account, discuss payment options, or answer any questions you might have about your debt. How can I assist you today?`,
        timestamp: new Date(),
      }]);
    } catch (err) {
      console.error('Failed to initialize chat:', err);
      // Still show welcome message on error
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hello ${debtor.first_name}! I'm your DFS Collections assistant. I'm here to help you understand your account, discuss payment options, or answer any questions you might have about your debt. How can I assist you today?`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: DisplayMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      // Call the agent with debtor context for automatic identification
      const response = await serenityClient.chat(
        AGENT_CODES.CHATBOT,
        userMessage.content,
        chatId || undefined,
        debtor.debtor_id,
        debtorContext // Pass context so agent knows who is talking
      );

      // Update chatId if we got a new session
      if (response.sessionId && !chatId) {
        setChatId(response.sessionId);
      }

      const assistantMessage: DisplayMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response || 'I apologize, but I was unable to process your request. Please try again.',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Store messages in database for history
      try {
        // Get case_id for the debtor
        let caseId = '';
        try {
          const cases = await serenityClient.queryData<{ case_id: string }>(
            `SELECT case_id FROM Cases WHERE debtor_id = '${debtor.debtor_id}' LIMIT 1`
          );
          caseId = cases[0]?.case_id || '';
        } catch {
          console.warn('Could not fetch case_id for chat');
        }
        
        await Promise.all([
          serenityClient.insertData('ChatMessages', {
            message_id: userMessage.id,
            case_id: caseId,
            debtor_id: debtor.debtor_id,
            session_id: chatId || response.sessionId,
            role: 'user',
            content: userMessage.content,
            created_at: userMessage.timestamp.toISOString(),
          }),
          serenityClient.insertData('ChatMessages', {
            message_id: assistantMessage.id,
            case_id: caseId,
            debtor_id: debtor.debtor_id,
            session_id: chatId || response.sessionId,
            role: 'assistant',
            content: assistantMessage.content,
            created_at: assistantMessage.timestamp.toISOString(),
          }),
        ]);
      } catch (dbErr) {
        // Don't fail the chat if DB storage fails
        console.warn('Failed to store chat messages:', dbErr);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      
      // Check if conversation was closed/finished by the agent
      const errorMessage = err instanceof Error ? err.message.toLowerCase() : '';
      const isConversationClosed = 
        errorMessage.includes('closed') || 
        errorMessage.includes('finished') ||
        errorMessage.includes('ended') ||
        errorMessage.includes('complete') ||
        errorMessage.includes('409') || // Conflict - often indicates closed conversation
        errorMessage.includes('not found');
      
      if (isConversationClosed) {
        // Conversation was finished by the agent - show friendly message
        setIsConversationEnded(true);
        setMessages(prev => [...prev, {
          id: `ended-${Date.now()}`,
          role: 'assistant',
          content: '✅ This conversation has been concluded. If you need further assistance, please start a new chat session using the "New Chat" button above.',
          timestamp: new Date(),
        }]);
        setError(null); // Don't show error banner for closed conversations
      } else {
        setError('Failed to send message. Please try again.');
        
        // Add error message
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'I apologize, but I encountered an error processing your request. Please try again or contact support if the issue persists.',
          timestamp: new Date(),
        }]);
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearChat = () => {
    // Reset chat and create new conversation
    setChatId(null);
    setError(null);
    setIsConversationEnded(false);
    initializeChat();
  };

  const suggestedQuestions = [
    "What is my current balance?",
    "Can I set up a payment plan?",
    "I'm experiencing financial hardship",
    "What payment methods do you accept?",
    "Can you explain the charges on my account?",
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Collections Assistant</h1>
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Online
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={clearChat}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="New conversation"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex gap-3 max-w-[80%] ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-blue-600' 
                      : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <div
                      className={`p-4 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      {message.role === 'user' ? (
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      ) : (
                        <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-strong:text-gray-900 prose-headings:text-gray-900 prose-table:my-2 prose-th:bg-gray-100 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-table:border prose-th:border prose-td:border">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${
                      message.role === 'user' ? 'text-right' : ''
                    } text-gray-400`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="flex gap-3 max-w-[80%]">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="p-4 bg-white border border-gray-200 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-3">Suggested questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInputMessage(question)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-t border-red-200 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {isConversationEnded ? (
            // Show "New Chat" button when conversation is ended
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">
                This conversation has ended. Start a new chat to continue.
              </p>
              <button
                onClick={clearChat}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Start New Chat
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="flex-1 p-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50"
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center mt-2">
                This AI assistant can help with payment options, account questions, and hardship requests.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
